import { useState } from "react";
import dayjs from "dayjs";

import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import Table from "react-bootstrap/Table";
import { FaEllipsisV, FaEye, FaInfoCircle, FaSyringe } from "react-icons/fa";

import { handleColumnToggle } from "@/utils/handleColumnTogle";
import { getAge } from "@/utils/dateUtil";
import {
	DetailedCitizenVaccinationsIndicatorC2Data,
	VaccineStatusIndicatorC2,
} from "@/services/esus/indicator/esf/c2/getDetailedCitizensVaccinationsIndicatorC2";

import { TableSkeletons } from "@/components/TableSkeletons";
import { CardOverlayTrigger } from "@/components/CardOverlayTrigger";

type Column = {
	title: string;
	visible: boolean;
};

type IndicatorEsfC2CitizensVaccinationsTableColumns = {
	citizenName: Column;
	citizenAge: Column;
	motherName: Column;
	healthTeam: Column;
	microarea: Column;
	pentaVaccination: Column;
	vipVaccination: Column;
	pneumoVaccination: Column;
	tripleViralVaccination: Column;
	refPentaVaccination: Column;
	refVipVaccination: Column;
	refPneumoVaccination: Column;
};

type IndicatorEsfC2CitizensVaccinationsTableProps = {
	citizensVaccinations: DetailedCitizenVaccinationsIndicatorC2Data[];
	isLoading: boolean;
	openCitizenHistory: (
		citizenWithVaccinations: DetailedCitizenVaccinationsIndicatorC2Data
	) => void;
};

export function getCitizenVaccinationStatusDescription(
	citizenVaccinationStatus?: VaccineStatusIndicatorC2
) {
	switch (citizenVaccinationStatus) {
		case "finished":
			return "Realizado";
		case "pending":
			return "Pendente";
		case "awaiting":
			return "Aguardando período";
		default:
			return "Fora do prazo";
	}
}

function getIconCitizenVaccinationStatus(citizenVaccinationStatus?: VaccineStatusIndicatorC2) {
	const title = getCitizenVaccinationStatusDescription(citizenVaccinationStatus);
	switch (citizenVaccinationStatus) {
		case "finished":
			return <FaSyringe title={title} className="text-primary fs-2" />;
		case "pending":
			return <FaSyringe title={title} className="text-warning fs-2" />;
		case "awaiting":
			return <FaSyringe title={title} className="text-muted fs-2" />;
		default:
			return <FaSyringe title={title} className="text-danger fs-2" />;
	}
}

export function IndicatorEsfC2CitizensVaccinationsTable({
	citizensVaccinations,
	isLoading,
	openCitizenHistory,
}: Readonly<IndicatorEsfC2CitizensVaccinationsTableProps>) {
	const [columns, setColumns] = useState<IndicatorEsfC2CitizensVaccinationsTableColumns>({
		citizenName: { title: "Nome do cidadão", visible: true },
		citizenAge: { title: "Idade", visible: true },
		motherName: { title: "Nome da mãe", visible: true },
		healthTeam: { title: "Equipe", visible: false },
		microarea: { title: "MA", visible: false },
		pentaVaccination: { title: "Penta", visible: true },
		vipVaccination: { title: "VIP", visible: true },
		pneumoVaccination: { title: "Pneumo", visible: true },
		tripleViralVaccination: { title: "Tríplice", visible: true },
		refPentaVaccination: { title: "Ref. Dtp", visible: true },
		refVipVaccination: { title: "Ref. Vip", visible: true },
		refPneumoVaccination: { title: "Ref. Pneumo", visible: true },
	});

	return (
		<Table striped bordered hover responsive>
			<thead className="text-center fs-7">
				<tr className="fw-bolder text-center">
					<th className={`text-start px-2 ${!columns.citizenName.visible && "d-none"}`}>
						{columns.citizenName.title}
					</th>
					<th className={`px-2 ${!columns.citizenAge.visible && "d-none"}`}>
						{columns.citizenAge.title}
					</th>
					<th className={`px-2 ${!columns.motherName.visible && "d-none"}`}>
						{columns.motherName.title}
					</th>
					<th className={`px-2 ${!columns.healthTeam.visible && "d-none"}`}>
						{columns.healthTeam.title}
					</th>
					<th className={`px-2 ${!columns.microarea.visible && "d-none"}`}>
						{columns.microarea.title}
					</th>
					<th className={`px-2 ${!columns.pentaVaccination.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.pentaVaccination.title}
							<CardOverlayTrigger
								header="Penta"
								message="Aplicações do imunobiológico(ou combinações) realizadas até 1 ano de idade"
								icon={<FaInfoCircle className="text-muted" />}
							/>
						</div>
					</th>
					<th className={`px-2 ${!columns.vipVaccination.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.vipVaccination.title}
							<CardOverlayTrigger
								header="Vip"
								message="Aplicações do imunobiológico(ou combinações) realizadas até 1 ano de idade"
								icon={<FaInfoCircle className="text-muted" />}
							/>
						</div>
					</th>
					<th className={`px-2 ${!columns.pneumoVaccination.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.pneumoVaccination.title}
							<CardOverlayTrigger
								header="Pneumocócica"
								message="Aplicações do imunobiológico(ou combinações) realizadas até 1 ano de idade"
								icon={<FaInfoCircle className="text-muted" />}
							/>
						</div>
					</th>
					<th className={`px-2 ${!columns.tripleViralVaccination.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.tripleViralVaccination.title}
							<CardOverlayTrigger
								header="Tríplice viral"
								message="Aplicações do imunobiológico(SCR ou SCRV) realizadas entre 1 e 2 anos de idade"
								icon={<FaInfoCircle className="text-muted" />}
							/>
						</div>
					</th>
					<th className={`px-2 ${!columns.refPneumoVaccination.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.refPneumoVaccination.title}
							<CardOverlayTrigger
								header="Reforço pneumocócica"
								message="Aplicação do imunobiológico com referência a dosagem 'R1' realizadas entre 1 e 2 anos de idade"
								icon={<FaInfoCircle className="text-muted" />}
							/>
						</div>
					</th>
					<th className={`px-2 ${!columns.refPentaVaccination.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.refPentaVaccination.title}
							<CardOverlayTrigger
								header="Reforço dtp"
								message="Aplicação do imunobiológico com referência a dosagem 'R1' realizadas de 1 ano e 3 meses à 2 anos de idade"
								icon={<FaInfoCircle className="text-muted" />}
							/>
						</div>
					</th>
					<th className={`px-2 ${!columns.refVipVaccination.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.refVipVaccination.title}
							<CardOverlayTrigger
								header="Reforço vip"
								message="Aplicação do imunobiológico com referência a dosagem 'R1' realizadas de 1 ano e 3 meses à 2 anos de idade"
								icon={<FaInfoCircle className="text-muted" />}
							/>
						</div>
					</th>
					<th className="text-nowrap pb-0">
						Ações
						<OverlayTrigger
							trigger="click"
							placement="bottom"
							overlay={
								<Popover>
									<Popover.Body>
										<Form className="d-flex flex-column gap-2">
											{Object.keys(columns).map((key, index) => (
												<Form.Check
													key={`citizen-attendances-table-column-${
														index + 1
													}`}
													type="checkbox"
													label={
														columns[
															key as keyof IndicatorEsfC2CitizensVaccinationsTableColumns
														].title
													}
													checked={
														columns[
															key as keyof IndicatorEsfC2CitizensVaccinationsTableColumns
														].visible
													}
													onChange={() =>
														handleColumnToggle(
															key as keyof IndicatorEsfC2CitizensVaccinationsTableColumns,
															setColumns
														)
													}
												/>
											))}
										</Form>
									</Popover.Body>
								</Popover>
							}
							rootClose
						>
							<Button
								title="Exibição de colunas da tabela"
								variant="white"
								className="px-2"
								disabled={isLoading}
							>
								<FaEllipsisV />
							</Button>
						</OverlayTrigger>
					</th>
				</tr>
			</thead>
			<tbody className="fs-8">
				{!isLoading && citizensVaccinations.length > 0
					? citizensVaccinations.map((citizenWithVaccinations, index) => (
							<tr key={`citizen-vaccinations-table-row-${index + 1}`}>
								<td className={`px-2 ${!columns.citizenName.visible && "d-none"}`}>
									<div className="d-flex flex-column">
										<div>{citizenWithVaccinations.fullName}</div>
										<div className="text-muted">
											{citizenWithVaccinations.birthdate
												? dayjs(citizenWithVaccinations.birthdate).format(
														"DD/MM/YYYY"
												  )
												: ""}
										</div>
									</div>
								</td>
								<td
									className={`text-center text-nowrap px-2 ${
										!columns.citizenAge.visible && "d-none"
									}`}
								>
									{getAge(citizenWithVaccinations.birthdate)}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.motherName.visible && "d-none"
									}`}
								>
									{citizenWithVaccinations.motherName ?? ""}
								</td>
								<td className={`px-2 ${!columns.healthTeam.visible && "d-none"}`}>
									<div className="d-flex flex-column">
										<div>{citizenWithVaccinations.healthTeam?.name ?? ""}</div>
										<div className="text-muted">
											{citizenWithVaccinations.healthTeam?.ine ?? ""}
										</div>
									</div>
								</td>
								<td
									className={`text-center text-nowrap px-2 ${
										!columns.microarea.visible && "d-none"
									}`}
								>
									{citizenWithVaccinations.area ?? "FA"}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.pentaVaccination.visible && "d-none"
									}`}
								>
									{Array.from({ length: 3 }).map((_, index) =>
										getIconCitizenVaccinationStatus(
											citizenWithVaccinations.vaccinationResume.penta > index
												? "finished"
												: citizenWithVaccinations.vaccinationResume
														.pentaStatus
										)
									)}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.vipVaccination.visible && "d-none"
									}`}
								>
									{Array.from({ length: 3 }).map((_, index) =>
										getIconCitizenVaccinationStatus(
											citizenWithVaccinations.vaccinationResume.vip > index
												? "finished"
												: citizenWithVaccinations.vaccinationResume
														.vipStatus
										)
									)}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.pneumoVaccination.visible && "d-none"
									}`}
								>
									{Array.from({ length: 2 }).map((_, index) =>
										getIconCitizenVaccinationStatus(
											citizenWithVaccinations.vaccinationResume.pneumococica >
												index
												? "finished"
												: citizenWithVaccinations.vaccinationResume
														.pneumococicaStatus
										)
									)}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.tripleViralVaccination.visible && "d-none"
									}`}
								>
									{Array.from({ length: 2 }).map((_, index) =>
										getIconCitizenVaccinationStatus(
											citizenWithVaccinations.vaccinationResume.tripleViral >
												index
												? "finished"
												: citizenWithVaccinations.vaccinationResume
														.tripleViralStatus
										)
									)}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.refPneumoVaccination.visible && "d-none"
									}`}
								>
									{Array.from({ length: 1 }).map((_, index) =>
										getIconCitizenVaccinationStatus(
											citizenWithVaccinations.vaccinationResume
												.refPneumococica > index
												? "finished"
												: citizenWithVaccinations.vaccinationResume
														.refPneumococicaStatus
										)
									)}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.refPentaVaccination.visible && "d-none"
									}`}
								>
									{Array.from({ length: 1 }).map((_, index) =>
										getIconCitizenVaccinationStatus(
											citizenWithVaccinations.vaccinationResume.refPenta >
												index
												? "finished"
												: citizenWithVaccinations.vaccinationResume
														.refPentaStatus
										)
									)}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.refVipVaccination.visible && "d-none"
									}`}
								>
									{Array.from({ length: 1 }).map((_, index) =>
										getIconCitizenVaccinationStatus(
											citizenWithVaccinations.vaccinationResume.refVip > index
												? "finished"
												: citizenWithVaccinations.vaccinationResume
														.refVipStatus
										)
									)}
								</td>
								<td className="text-center">
									<Button
										className="bg-primary py-1 px-3"
										onClick={() => openCitizenHistory(citizenWithVaccinations)}
										title="Histórico de visitas do ACS"
									>
										<FaEye className="fs-4" />
									</Button>
								</td>
							</tr>
					  ))
					: isLoading && (
							<TableSkeletons
								numberOfCells={
									Object.values(columns).reduce(
										(count, column) =>
											count + (column?.visible === true ? 1 : 0),
										0
									) + 1
								}
							/>
					  )}
			</tbody>
		</Table>
	);
}
